import { CompanyBusinessMode } from '~/swagger/portal/Api';

/**
 * HOA mode is keyed off the host's first label: the HOA experience is served
 * from an `hoa.` host (e.g. `hoa.magicdoor-test.com`), the property-management
 * portal from the sibling `portal.` host (e.g. `portal.magicdoor-test.com`).
 * They share the same root domain but are distinct hosts (= distinct origins =
 * separate auth sessions), so a single deployment serves BOTH an HOA login and a
 * PM login side by side without one bleeding into the other.
 *
 * It is evaluated synchronously at module load — BEFORE the company fetch
 * resolves — so the theme, labels, document title and nav can all be applied on
 * the very first render with no flash. Once the company loads, the backend's
 * `businessMode` is authoritative: at login {@link redirectToBusinessModeHostOnLogin}
 * sends the user to the matching host if they signed into the wrong one.
 *
 * Local dev behaves identically: Chromium browsers resolve `*.localhost` to
 * 127.0.0.1 automatically (RFC 6761), so `http://hoa.localhost:3100` hits the
 * same dev server and the same redirect logic runs. (Firefox/Safari need a
 * one-line `/etc/hosts` entry: `127.0.0.1 hoa.localhost portal.localhost`.)
 */
const HOA_LABEL = 'hoa';
const PM_LABEL = 'portal';

const hostIsHoa = (hostname: string): boolean => hostname.split('.')[0] === HOA_LABEL;

/**
 * Whether `hostname` is one of the two recognized portal hosts (first label `hoa`
 * or `portal`). Exported for unit tests; production reads {@link IS_PORTAL_HOST}.
 */
export const hostIsPortal = (hostname: string): boolean => {
  const firstLabel = hostname.split('.')[0];
  return firstLabel === HOA_LABEL || firstLabel === PM_LABEL;
};

const swapHostLabel = (hostname: string, label: string): string => {
  const parts = hostname.split('.');
  // A single-label host (bare `localhost`, an IP) has no sibling to swap to —
  // there's nothing meaningful to redirect to, so leave it untouched.
  if (parts.length < 2) return hostname;
  parts[0] = label;
  return parts.join('.');
};

export const IS_HOA_DEMO_MODE = typeof location !== 'undefined' && hostIsHoa(location.hostname);

/**
 * Whether this host is one of the two recognized portal hosts (`hoa.<domain>` /
 * `portal.<domain>`), i.e. part of a dual-host deployment with a real sibling to
 * cross to. False for bare `localhost`, an IP, or any other single-portal host —
 * where there is no sibling portal and host-based account correction must NOT run,
 * or it would treat a legitimately signed-in sibling-mode account as "wrong host".
 */
export const IS_PORTAL_HOST = typeof location !== 'undefined' && hostIsPortal(location.hostname);

/** The HOA-mode host for a given host (swaps the first label to `hoa`). */
export const toHoaHost = (hostname: string): string => swapHostLabel(hostname, HOA_LABEL);

/** The PM-mode host for a given host (swaps the first label to `portal`). */
export const toPropertyManagementHost = (hostname: string): string => swapHostLabel(hostname, PM_LABEL);

/** True when the loaded company is configured for HOA on the backend. */
export const isCompanyHoaMode = (businessMode: CompanyBusinessMode | undefined): boolean => businessMode === CompanyBusinessMode.Hoa;

/** Countdown shown on the "Redirecting to …" interstitial before navigating. */
const REDIRECT_COUNTDOWN_FROM = 3;

const HOA_GREEN_BRAND = '#1F8A6A';
const PM_BLUE_BRAND = '#3B1FA8';

/**
 * Show a full-screen "Redirecting to <X> Portal in 3… 2… 1…" notice that counts
 * down, then navigate to `targetUrl`. Rendered as raw DOM (not a Solid component)
 * so it works no matter where the app is in its render lifecycle — same approach
 * as {@link installHoaThemeOverrides}. Idempotent: a second call is a no-op while
 * a redirect is already pending.
 *
 * Used both by the login-time handoff and by the cross-host account switcher, so
 * a cross-host hop always shows the same countdown UX.
 */
export const showRedirectNotice = (targetIsHoa: boolean, targetUrl: string): void => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('hoa-redirect-notice')) return;

  const portalName = targetIsHoa ? 'HOA Portal' : 'Company Portal';
  const accent = targetIsHoa ? HOA_GREEN_BRAND : PM_BLUE_BRAND;

  const overlay = document.createElement('div');
  overlay.id = 'hoa-redirect-notice';
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:24px',
    'background:#ffffff',
    'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
  ].join(';');
  overlay.innerHTML = `
    <div style="position:relative;display:flex;align-items:center;justify-content:center;width:88px;height:88px">
      <div style="position:absolute;inset:0;border:3px solid ${accent}26;border-top-color:${accent};border-radius:50%;animation:hoa-redirect-spin .9s linear infinite"></div>
      <div id="hoa-redirect-count" style="font-size:34px;font-weight:700;color:${accent}">${REDIRECT_COUNTDOWN_FROM}</div>
    </div>
    <div style="font-size:16px;font-weight:600;color:#1f2937">Redirecting to ${portalName}…</div>
    <style>@keyframes hoa-redirect-spin{to{transform:rotate(360deg)}}</style>
  `;
  document.body.appendChild(overlay);

  const countEl = overlay.querySelector<HTMLElement>('#hoa-redirect-count');
  let remaining = REDIRECT_COUNTDOWN_FROM;
  const tick = window.setInterval(() => {
    remaining -= 1;
    if (remaining > 0) {
      if (countEl) countEl.textContent = String(remaining);
      return;
    }
    window.clearInterval(tick);
    location.assign(targetUrl);
  }, 1000);
};

/** The route on the target host that redeems a handoff interaction code. */
export const HANDOFF_PATH = '/auth/handoff';

/** Mints a fresh, single-use interaction code for the current session. */
export type MintInteractionCode = () => Promise<string>;

/**
 * Called at LOGIN time, once the just-authenticated company is known. If that
 * company's backend `businessMode` belongs to the OTHER host (HOA company on the
 * Company host, or vice versa), hand the session off to the matching host and
 * return `true` so the caller skips its own navigation. When the company matches
 * the current host, return `false` — a normal login proceeds with no interstitial.
 *
 * The two hosts are separate origins with separate token stores, so we can't just
 * swap the hostname (the target would be logged out). Instead we mint a fresh,
 * single-use interaction code for the current session and pass it to the target
 * host's {@link HANDOFF_PATH}, which redeems it via the normal OAuth flow to mint
 * its own session — silently, no credentials re-entry, no shared cookie. The code
 * rides in the URL *fragment* (never sent to servers / logs) and the handoff route
 * strips it on arrival.
 *
 * Runs only on the login completion path — NOT on every company load — so a
 * routine refresh or in-app navigation never triggers a handoff.
 */
export const redirectToBusinessModeHostOnLogin = async (
  businessMode: CompanyBusinessMode | undefined,
  postLoginPath: string,
  mintInteractionCode: MintInteractionCode,
  /**
   * This host's cross-host account roster (JSON), handed to the target host so it
   * can show our accounts in its switcher even without the shared cookie. Rides in
   * the fragment alongside the interaction code, so it never hits the server.
   */
  roster?: string
): Promise<boolean> => {
  if (typeof location === 'undefined' || businessMode === undefined) return false;
  const companyIsHoa = isCompanyHoaMode(businessMode);
  if (companyIsHoa === IS_HOA_DEMO_MODE) return false;

  const targetHost = companyIsHoa ? toHoaHost(location.hostname) : toPropertyManagementHost(location.hostname);
  if (targetHost === location.hostname) return false;

  // Resolve `postLoginPath` against the CURRENT origin first, then keep only its
  // path/query/hash — so a stray absolute URL can never point the handoff at an
  // arbitrary domain. The final destination after redemption.
  const resolved = new URL(postLoginPath, location.origin);
  const intendedPath = resolved.pathname + resolved.search + resolved.hash;
  const rosterFragment = roster ? `&roster=${encodeURIComponent(roster)}` : '';

  let interactionCode: string;
  try {
    interactionCode = await mintInteractionCode();
  } catch {
    // Couldn't mint a code — fall back to a normal navigation on the target host;
    // the user will land on its login page rather than a broken silent handoff.
    const fallback = new URL(intendedPath, location.origin);
    fallback.hostname = targetHost;
    showRedirectNotice(companyIsHoa, fallback.toString());
    return true;
  }

  const handoff = new URL(HANDOFF_PATH, location.origin);
  handoff.hostname = targetHost;
  // Fragment, not query: never sent to servers/logs; stripped on arrival.
  handoff.hash = `interaction_code=${encodeURIComponent(interactionCode)}&redirect=${encodeURIComponent(intendedPath)}${rosterFragment}`;
  showRedirectNotice(companyIsHoa, handoff.toString());
  return true;
};

/** Locale ids we localize HOA mode for. Mirror the i18n locale codes. */
export type HoaLocale = 'en' | 'zh-cn' | 'es';

/**
 * Per-locale vocabulary swap for HOA mode, applied to EVERY translation string
 * (see {@link applyHoaLabelOverrides}). This is the workhorse: it renames the
 * property-management vocabulary to HOA vocabulary anywhere it appears, so we
 * don't hand-map every string. {@link HOA_LABEL_OVERRIDES_BY_LOCALE} layers
 * exact-phrase replacements on top for marketing copy / phrasings the word swap
 * can't capture.
 *
 * Order matters: MULTI-WORD / longer terms come before the shorter ones they
 * contain (e.g. "Property manager" before "Property"); entries apply in order.
 *
 * English/Spanish are space-delimited, so they match on WHOLE WORDS (boundaries)
 * and preserve the matched casing. Chinese is logographic with no word
 * boundaries or casing, so it uses plain substring replacement of the surface
 * forms that actually appear in the zh translations.
 */
const HOA_WORD_REPLACEMENTS_BY_LOCALE: Record<HoaLocale, ReadonlyArray<readonly [string, string]>> = {
  en: [
    // Multi-word first (longest, most specific).
    ['property manager', 'HOA manager'],
    ['property managers', 'HOA managers'],
    ['rental application', 'membership application'],
    ['rental applications', 'membership applications'],
    ['rental payment', 'dues payment'],
    ['rental payments', 'dues payments'],
    ['lease renewal', 'membership renewal'],
    ['lease template', 'template'],
    ['lease templates', 'templates'],
    ['monthly rent', 'monthly dues'],
    ['rent payment', 'dues payment'],
    ['rent payments', 'dues payments'],
    ['move-in', 'membership start'],
    ['move-out', 'membership end'],
    ['owner funds', 'reserve fund'],
    // Single words.
    ['properties', 'HOAs'],
    ['property', 'HOA'],
    ['portfolios', 'HOA groups'],
    ['portfolio', 'HOA group'],
    ['tenants', 'members'],
    ['tenant', 'member'],
    ['owners', 'board members'],
    ['owner', 'board member'],
    ['leases', 'member accounts'],
    ['leasing', 'member accounts'],
    ['lease', 'member account'],
    ['landlord', 'association'],
    ['rent', 'dues'],
    ['eviction', 'suspension'],
    ['evict', 'suspend'],
  ],
  es: [
    ['administrador de propiedades', 'administrador de la HOA'],
    ['administradores de propiedades', 'administradores de la HOA'],
    ['solicitud de alquiler', 'solicitud de membresía'],
    ['solicitudes de alquiler', 'solicitudes de membresía'],
    ['alquiler mensual', 'cuotas mensuales'],
    ['propiedades', 'HOAs'],
    ['propiedad', 'HOA'],
    ['carteras', 'grupos de HOA'],
    ['cartera', 'grupo de HOA'],
    ['inquilinos', 'miembros'],
    ['inquilino', 'miembro'],
    ['propietarios', 'miembros de la junta'],
    ['propietario', 'miembro de la junta'],
    ['contratos de arrendamiento', 'cuentas de miembro'],
    ['contrato de arrendamiento', 'cuenta de miembro'],
    ['contratos', 'cuentas de miembro'],
    ['arrendamiento', 'cuenta de miembro'],
    ['alquiler', 'cuotas'],
    ['desalojo', 'suspensión'],
  ],
  // No replacement may contain another rule's SOURCE term, or chained substring
  // replacement would double-apply (e.g. mapping 房产 -> 业主协会 would then get
  // re-hit by the 业主 rule). Targets below are deliberately collision-free.
  'zh-cn': [
    ['物业经理', 'HOA 经理'],
    ['物业管理人', 'HOA 管理人'],
    ['物业管理', 'HOA 管理'],
    ['租赁申请', '会籍申请'],
    ['月租', '月会费'],
    ['房产', '社区'],
    ['物业', '社区'],
    ['租户', '成员'],
    ['业主', '理事会成员'],
    ['租约', '会员账户'],
    ['租赁', '会员账户'],
    ['租金', '会费'],
    ['投资组合', 'HOA 分组'],
    ['项目', 'HOA 分组'],
    ['驱逐', '暂停会籍'],
  ],
};

/** Match a source word/phrase as a whole word (boundaries), case-insensitively. */
const wordRegex = (term: string): RegExp => new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');

/** Match a source term anywhere (no word boundaries — for logographic scripts). */
const substringRegex = (term: string): RegExp => new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');

/** Re-apply the casing of `source` (the matched text) onto `replacement`. */
const matchCase = (source: string, replacement: string): string => {
  if (source === source.toUpperCase() && source !== source.toLowerCase()) return replacement.toUpperCase();
  if (source[0] === source[0]?.toUpperCase()) return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  return replacement;
};

/** Whether a locale uses word boundaries + casing (true) or plain substrings (false). */
const localeUsesWordBoundaries = (locale: HoaLocale): boolean => locale !== 'zh-cn';

const buildMatchers = (locale: HoaLocale) =>
  HOA_WORD_REPLACEMENTS_BY_LOCALE[locale].map(([term, replacement]) => ({
    re: localeUsesWordBoundaries(locale) ? wordRegex(term) : substringRegex(term),
    replacement,
    cased: localeUsesWordBoundaries(locale),
  }));

const HOA_WORD_MATCHERS_BY_LOCALE: Record<HoaLocale, ReturnType<typeof buildMatchers>> = {
  en: buildMatchers('en'),
  es: buildMatchers('es'),
  'zh-cn': buildMatchers('zh-cn'),
};

/**
 * Apply the per-locale whole-word HOA vocabulary swap to a single string.
 * `locale` defaults to English. Exported for unit tests; production code goes
 * through {@link applyHoaLabelOverrides}.
 */
export const applyHoaWordSwap = (value: string, locale: HoaLocale = 'en'): string => {
  let result = value;
  for (const { re, replacement, cased } of HOA_WORD_MATCHERS_BY_LOCALE[locale]) {
    re.lastIndex = 0;
    result = result.replace(re, (match) => (cased ? matchCase(match, replacement) : replacement));
  }
  return result;
};

/**
 * Exact-phrase overrides per locale, layered on top of the word swap so marketing
 * copy and special phrasings win over the generic vocabulary substitution. English
 * is the complete set; zh-cn / es cover the domain and structural phrases (the
 * word swap handles the bulk vocabulary in those locales). Any phrase not listed
 * for a locale falls through to its word-swapped value.
 */
const HOA_LABEL_OVERRIDES_EN: Record<string, string> = {
  tenant: 'Member',
  tenants: 'Members',
  Owner: 'Board Member',
  Owners: 'Board Members',
  owner: 'Board Member',
  owners: 'Board Members',
  Leasing: 'Member Accounts',
  leasing: 'Member Accounts',
  Portfolios: 'HOA Groups',
  Portfolio: 'HOA Group',
  'Leases with balance due': 'Members with balance due',
  'Get paid faster — and earn while you do': 'Collect HOA dues faster — and earn while you do',
  'Online Payments — your edge over checks': 'Online Payments — modernize HOA dues collection',
  'Get paid faster': 'Collect dues faster',
  Balance: 'Outstanding balance',
  'Late rent is costing you': 'Late dues are costing you',
  'Create a portfolio': 'Create an HOA group',
  'Add a property': 'Add an HOA',
  'Select a property type': 'Select an HOA type',
  Leases: 'Member Accounts',
  'Lease details': 'Account details',
  'Lease overview': 'Account overview',
  'Lease start': 'Membership start',
  'Lease end': 'Membership end',
  'Lease start date': 'Membership start date',
  'Lease end date': 'Membership end date',
  'Start date': 'Membership start',
  'End date': 'Membership end',
  'Lease term': 'Membership status',
  'Active leases': 'Active members',
  'Expired leases': 'Inactive members',
  'Draft leases': 'Pending members',
  'Lease type': 'Account type',
  'Lease renewal': 'Renewal',
  'Lease ledger': 'Member ledger',
  'Lease balance': 'Account balance',
  'Lease charges': 'Dues & assessments',
  'Lease charge': 'Dues / assessment',
  'Add a manager': 'Add an HOA manager',
  'Note: Tenants without an email or lease set cannot be invited.':
    'Note: Members without an email or property record set cannot be invited.',
  'Move-in': 'Member move-in',
  'Move-out': 'Member move-out',
  'Owner portal': 'Board portal',
  'No leases with balance due': 'No members with balance due',
  'Add a property manager': 'Add an HOA manager',
  'Notice on Rent Management Settings': 'Notice on dues management settings',
  Company: 'Association',
  'Company information': 'Association information',
  'Set up company information and working hours': 'Set up association information and working hours',
  'Portal management': 'Member portal management',
  'Portal invite & url': 'Member portal invite & url',
  'Portal invite & text': 'Member portal invite & text',
  'Portal settings': 'Member portal settings',
  'Style settings for the portal': 'Style settings for the member portal',
  'Modify your URL': 'Modify your member portal URL',
  'Portal invite notification texts': 'Member portal invite notification texts',
  'Chat settings': 'Communications settings',
  'No Leases': 'No member accounts',
  'No lease expiring': 'No memberships expiring',
  'No Property Yet': 'No HOA yet',
  'No active-lease tenants to announce to.': 'No active members to announce to.',
  'No templates available, start by adding a new template here': 'No templates available, start by adding a new one here',
  'Active leases count': 'Active member accounts',
  'Properties in this portfolio': 'HOAs in this group',
  'Add property to portfolio': 'Add HOA to group',
  'Properties (count)': 'HOAs',
  'Total bills': 'Total HOA expenses',
  'Total payments received': 'Total dues received',
  'Pending payments': 'Pending dues',
  'Late payments': 'Late dues',
  'Recurring offline payments': 'Recurring dues',
  'Recurring charges': 'Recurring dues',
  'Auto pay': 'Auto-pay dues',
  'Setup auto pay': 'Set up auto-pay dues',
  'Lease payment': 'Dues payment',
  'Lease payments': 'Dues payments',
  "Your tenants aren't the only ones who know you take checks. Roughly 1 in 3 property managers reports a mailbox or office break-in tied to paper payments each year.":
    "Your members aren't the only ones who know you accept checks. Roughly 1 in 3 HOAs reports a mailbox or office break-in tied to paper payments each year.",
  'Surcharging ACH and card payments can add <b>~14%</b> to your income — without raising your management fee.':
    'Surcharging ACH and card payments can add <b>~14%</b> to your association revenue — without raising HOA dues.',
  ' in new revenue.': ' in new association revenue.',
  'Start eviction': 'Suspend member',
  'End lease': 'Close member account',
  'Add tenant to lease': 'Add member to account',
  'There are no leases under this unit': 'There are no member accounts for this unit',
  'Time is magic': 'Community made simple',
  'AI-automated rental management that simplifies your operations': 'AI-powered HOA management for boards, members, and managers',
  Distributions: 'Reserve distributions',
  Contributions: 'Special assessments',
  Distribution: 'Reserve distribution',
  Contribution: 'Special assessment',
  'Late fees': 'Late dues fees',
  'Late fees overrides': 'Late dues fees overrides',
  'Late fees settings saved successfully': 'Late dues fees settings saved',
  'Late fees settings deleted successfully': 'Late dues fees settings deleted',
  'Late fees charged report': 'Late dues fees report',
  'Late fees including paid': 'Late dues fees including paid',
  'Late fees including manual charges': 'Late dues fees including manual charges',
  'Rent ledger': 'Member ledger',
  'Rent roll': 'HOA roll',
  'Owner directory': 'Board directory',
  'Lease unpaid bills': 'Member unpaid dues',
  Bills: 'HOA expenses',
  'Vendor bill': 'HOA expense',
  'Vendor bills': 'HOA expenses',
  'Add vendor bill': 'Add HOA expense',
  'Create vendor bill': 'Create HOA expense',
  'Pay vendor bills': 'Pay HOA expenses',
  'Portfolio/property access': 'HOA group / HOA access',
  OwnershipPercentage: 'Ownership %',
  'Owner type': 'Board role',
};

const HOA_LABEL_OVERRIDES_ZH: Record<string, string> = {
  公司: '协会',
  公司信息: '协会信息',
  账单: 'HOA 支出',
  供应商账单: 'HOA 支出',
  缴费: '特别评估费',
  收益: '储备金分配',
  滞纳金: '逾期会费',
  业主目录: '理事会目录',
  业主门户: '理事会门户',
  租约续订: '续期',
};

const HOA_LABEL_OVERRIDES_ES: Record<string, string> = {
  Empresa: 'Asociación',
  'Información de la empresa': 'Información de la asociación',
  Facturas: 'Gastos de la HOA',
  Contribuciones: 'Evaluaciones especiales',
  Distribuciones: 'Distribuciones de reserva',
  'Cargos por retraso': 'Cargos por cuotas vencidas',
  'Renovación de contrato': 'Renovación',
};

/** Exact-phrase override sets keyed by locale. */
const HOA_LABEL_OVERRIDES_BY_LOCALE: Record<HoaLocale, Record<string, string>> = {
  en: HOA_LABEL_OVERRIDES_EN,
  es: HOA_LABEL_OVERRIDES_ES,
  'zh-cn': HOA_LABEL_OVERRIDES_ZH,
};

/** English overrides remain exported for the regression test / external reference. */
export const HOA_LABEL_OVERRIDES = HOA_LABEL_OVERRIDES_EN;

const HOA_HIDDEN_MENU_IDS = new Set<string>([
  'Listing',
  'Subsidizers',
  'Insurance',
  'Lease renewal',
  'Rental applications',
  'Add offline payments',
  'Lease templates',
  'AI runbooks',
]);

export const isHoaMenuItemHidden = (id: string): boolean => {
  if (!IS_HOA_DEMO_MODE) return false;
  return HOA_HIDDEN_MENU_IDS.has(id);
};

const HOA_ONLY_MENU_IDS = new Set<string>(['Architectural requests', 'Board members', 'Surveys']);

export const isHoaOnlyMenuItem = (id: string): boolean => HOA_ONLY_MENU_IDS.has(id);

export type HoaInjectedChild = { id: string; path: string };

export const getHoaInjectedChildren = (_parentId: string): HoaInjectedChild[] => {
  return [];
};

const HOA_HIDDEN_DASHBOARD_KEYS = new Set<string>(['Insurance', 'RentalApplications', 'PaidSupport', 'ReturnsAndDisputes']);

const HOA_HIDDEN_SETTINGS_GROUPS = new Set<string>(['Rental Application', 'Insurance']);
const HOA_HIDDEN_SETTINGS_PATHS = new Set<string>([
  'lease/syndication',
  'lease/renewal',
  'lease/automatic-lease-renewal',
  'lease/auto-pay-incentive',
  'lease/lease-invoice',
  'lease/renters-insurance',
  'insurance/damage-waivers',
  'property/rent-payment-processing-fees',
  // Rental Application children — the group is hidden on the overview, but list
  // the child paths too so any per-path check (e.g. direct navigation guards)
  // also treats them as hidden in HOA mode. Mirrors how Insurance is handled.
  'rental/applicationfee',
  'rental/information',
  'rental/chart-of-account',
  'rental/general-settings',
  'rental/standard-questions',
]);

export const isHoaSettingsGroupHidden = (labelKey: string): boolean => {
  if (!IS_HOA_DEMO_MODE) return false;
  return HOA_HIDDEN_SETTINGS_GROUPS.has(labelKey);
};

export const isHoaSettingsChildHidden = (path: string): boolean => {
  if (!IS_HOA_DEMO_MODE) return false;
  return HOA_HIDDEN_SETTINGS_PATHS.has(path);
};

export const isHoaDashboardModuleHidden = (key: string): boolean => {
  if (!IS_HOA_DEMO_MODE) return false;
  return HOA_HIDDEN_DASHBOARD_KEYS.has(key);
};

export const HOA_DASHBOARD_TITLE_OVERRIDES: Record<string, { title?: string; description?: string }> = {
  OnlinePaymentSales: {
    title: 'Collect dues faster',
    description: 'Compare your online dues-collection rate to the platform and convert members over with one click',
  },
  OnTimeRent: {
    title: 'On-time dues',
    description: 'Share of HOA dues paid by the grace deadline in the last 30 days',
  },
  LeasesWithBalanceDue: {
    title: 'Members with balance due',
    description: 'Members with outstanding HOA dues or assessments',
  },
  DailyMoneyMovement: {
    title: 'HOA cash flow',
    description: 'Daily money in and out for your associations in the last 30 days',
  },
};

export const applyHoaLabelOverrides = <T extends Record<string, string>>(translations: T, locale: HoaLocale = 'en'): T => {
  if (!IS_HOA_DEMO_MODE) return translations;
  // 1) Per-locale whole-word vocabulary swap across every string (covers the long
  //    tail of labels we never hand-mapped).
  // 2) Per-locale exact-phrase overrides layered on top, so marketing copy and
  //    special phrasings win over the generic word swap.
  const swapped = {} as Record<string, string>;
  for (const key in translations) swapped[key] = applyHoaWordSwap(translations[key], locale);
  return { ...swapped, ...HOA_LABEL_OVERRIDES_BY_LOCALE[locale] } as T;
};

const HOA_GREEN = '#1F8A6A';
const HOA_GREEN_HOVER = '#2EA67F';
const HOA_GREEN_ACTIVE = '#146049';
const HOA_GREEN_LIGHT = 'rgba(31, 138, 106, 0.12)';
const HOA_GREEN_LIGHT_ACTIVE = 'rgba(31, 138, 106, 0.24)';

export const installHoaThemeOverrides = (): void => {
  if (!IS_HOA_DEMO_MODE) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById('hoa-demo-theme-overrides')) return;

  const style = document.createElement('style');
  style.id = 'hoa-demo-theme-overrides';
  style.textContent = `
    .bg-primary { background-color: ${HOA_GREEN} !important; }
    .bg-primary-hover { background-color: ${HOA_GREEN_HOVER} !important; }
    .bg-primary-active { background-color: ${HOA_GREEN_ACTIVE} !important; }
    .bg-primary-light { background-color: ${HOA_GREEN_LIGHT} !important; }
    .bg-primary-light-active { background-color: ${HOA_GREEN_LIGHT_ACTIVE} !important; }
    .bg-primary-alpha { background-color: ${HOA_GREEN_LIGHT} !important; }
    .text-primary { color: ${HOA_GREEN} !important; }
    .text-primary-hover { color: ${HOA_GREEN_HOVER} !important; }
    .text-primary-active { color: ${HOA_GREEN_ACTIVE} !important; }
    .border-primary { border-color: ${HOA_GREEN} !important; }
    .border-primary-hover { border-color: ${HOA_GREEN_HOVER} !important; }
    .border-primary-active { border-color: ${HOA_GREEN_ACTIVE} !important; }
    .ring-primary { --tw-ring-color: ${HOA_GREEN} !important; }
    .fill-primary { fill: ${HOA_GREEN} !important; }
    .stroke-primary { stroke: ${HOA_GREEN} !important; }
    .from-primary { --tw-gradient-from: ${HOA_GREEN} !important; }
    .to-primary { --tw-gradient-to: ${HOA_GREEN} !important; }
    .via-primary { --tw-gradient-stops: var(--tw-gradient-from), ${HOA_GREEN}, var(--tw-gradient-to) !important; }
    .hover\\:bg-primary:hover { background-color: ${HOA_GREEN} !important; }
    .hover\\:bg-primary-hover:hover { background-color: ${HOA_GREEN_HOVER} !important; }
    .hover\\:bg-primary-active:hover { background-color: ${HOA_GREEN_ACTIVE} !important; }
    .hover\\:text-primary:hover { color: ${HOA_GREEN} !important; }
    .hover\\:border-primary:hover { border-color: ${HOA_GREEN} !important; }
    .bg-gradient-dark-purple-blue { background-image: linear-gradient(45deg, #0B3B2E, #146049) !important; }
    [class*="bg-primary/"] { background-color: ${HOA_GREEN_LIGHT} !important; }
    [class*="text-primary/"] { color: ${HOA_GREEN} !important; }
    [class*="border-primary/"] { border-color: ${HOA_GREEN} !important; }
    .from-\\[\\#3B1FA8\\] { --tw-gradient-from: ${HOA_GREEN_ACTIVE} !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgb(11 59 46 / 0)) !important; }
    .via-primary { --tw-gradient-stops: var(--tw-gradient-from), ${HOA_GREEN}, var(--tw-gradient-to, rgb(11 59 46 / 0)) !important; }
    .to-\\[\\#A289FF\\] { --tw-gradient-to: ${HOA_GREEN_HOVER} !important; }
    .from-\\[\\#ee3964\\] { --tw-gradient-from: ${HOA_GREEN_ACTIVE} !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgb(11 59 46 / 0)) !important; }
    .to-\\[\\#ec4494\\] { --tw-gradient-to: ${HOA_GREEN_HOVER} !important; }
    .from-\\[\\#7717DD\\] { --tw-gradient-from: ${HOA_GREEN_ACTIVE} !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgb(11 59 46 / 0)) !important; }
    .to-\\[\\#A938FF\\] { --tw-gradient-to: ${HOA_GREEN_HOVER} !important; }
  `;
  document.head.appendChild(style);
};
